const API_BASE = `${import.meta.env.VITE_API_URL || "http://localhost:3000"}/api/v1`;

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  getToken() {
    return this.token;
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      ...((options.headers as Record<string, string>) || {}),
    };

    // Solo declarar JSON cuando REALMENTE hay body. Si mandamos
    // Content-Type: application/json en un request sin cuerpo (DELETE, o un
    // PATCH/POST sin body), Fastify responde 400 FST_ERR_CTP_EMPTY_JSON_BODY
    // ("Body cannot be empty when content-type is set to 'application/json'").
    // Esto rompía el delete y las asignaciones sin body.
    if (options.body != null && !("Content-Type" in headers)) {
      headers["Content-Type"] = "application/json";
    }

    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });

    if (res.status === 401) {
      this.token = null;
      localStorage.removeItem("rad_token");
      window.location.href = "/login";
      throw new Error("Unauthorized");
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }));
      const errMsg =
        typeof body.error === "string"
          ? body.error
          : body.error?.message || body.message || `HTTP ${res.status}`;
      throw new Error(errMsg);
    }

    const json = await res.json();
    // API returns { success, data } envelope — unwrap it
    if (json && typeof json === "object" && "data" in json) {
      return json.data as T;
    }
    return json as T;
  }

  get<T>(path: string) {
    return this.request<T>(path);
  }

  /** GET for list endpoints — auto-extracts `items` from paginated `{ items, total }` responses */
  async getList<T>(path: string): Promise<T[]> {
    const result = await this.request<T[] | { items: T[] }>(path);
    if (Array.isArray(result)) return result;
    if (result && typeof result === "object" && "items" in result) {
      return (result as { items: T[] }).items;
    }
    return [];
  }

  /**
   * GET for paginated endpoints — keeps the `{ items, total, page, limit, hasMore }`
   * envelope so the UI can render counts and a "load more" control.
   */
  async getPage<T>(
    path: string,
  ): Promise<{ items: T[]; total: number; page: number; limit: number; hasMore: boolean }> {
    const result = await this.request<
      T[] | { items?: T[]; total?: number; page?: number; limit?: number; hasMore?: boolean }
    >(path);
    if (Array.isArray(result)) {
      return { items: result, total: result.length, page: 1, limit: result.length, hasMore: false };
    }
    const items = result?.items ?? [];
    return {
      items,
      total: result?.total ?? items.length,
      page: result?.page ?? 1,
      limit: result?.limit ?? items.length,
      hasMore: result?.hasMore ?? false,
    };
  }

  post<T>(path: string, body?: unknown) {
    return this.request<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  put<T>(path: string, body?: unknown) {
    return this.request<T>(path, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  patch<T>(path: string, body?: unknown) {
    return this.request<T>(path, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  delete<T>(path: string) {
    return this.request<T>(path, { method: "DELETE" });
  }
}

export const api = new ApiClient();
